import LegalLayout, { LegalHero, LegalSection, LegalList } from "@/components/LegalLayout";

export default function RefundPage() {
  return (
    <>
      <LegalHero
        eyebrow="COMMERCIAL · REFUND POLICY"
        title="Refund & Cancellation Policy"
        subtitle="Fair, clear, and consistent with how AI-generated SaaS pricing works."
        updated="2026"
      />
      <LegalLayout>
        <LegalSection id="digital-service" title="1. Digital Service Policy">
          <p>
            RADAR is an AI-powered digital service that provides on-demand intelligence reports,
            real-time web grounding, primary-data enhancement, and CRM pipeline tracking. Once a
            paid report or enhancement has been generated, the AI compute and third-party API
            costs (Anthropic Claude, Tavily search, Resend email) have already been incurred.
          </p>
          <p>
            As a result, <strong className="text-white">subscription fees are generally
            non-refundable once reports are generated</strong> within a billing cycle. This is
            standard SaaS practice for AI-consumption pricing.
          </p>
        </LegalSection>

        <LegalSection id="exceptions" title="2. Refund Exceptions">
          <p>We will issue a refund in the following situations:</p>
          <LegalList items={[
            "Duplicate charge — you were billed twice for the same subscription period.",
            "Failed subscription activation — payment processed but your account was never upgraded.",
            "Technical billing error — wrong amount charged or unexpected currency conversion.",
            "Service unavailability — RADAR was demonstrably down for a sustained portion (≥ 24 hours) of your billing cycle and we could not deliver core functionality.",
          ]} />
          <p>
            Refund requests must be submitted within <strong className="text-white">7 days</strong>
            of the charge. Approved refunds are returned to the original payment method within
            5–10 business days, depending on the gateway.
          </p>
        </LegalSection>

        <LegalSection id="cancellation" title="3. Cancellation">
          <p>
            You can cancel your subscription at any time, for any reason — no questions asked, no
            "are you sure?" gauntlet. Cancellation takes effect at the end of your current billing
            cycle; you keep full access (and your current quota) until that date.
          </p>
          <p>
            To cancel, sign in to your account, open the Pricing page, and switch to the Free plan,
            or email <a className="text-[#0A84FF] hover:underline" href="mailto:support@radaracctintel.com">support@radaracctintel.com</a>.
          </p>
        </LegalSection>

        <LegalSection id="expired" title="4. Expired Accounts">
          <p>
            After a paid subscription expires (whether by cancellation or non-renewal), your account
            enters read-only mode for 1 year:
          </p>
          <LegalList items={[
            "Historical reports remain visible and viewable.",
            "Past CRM pipeline data, BDR activity, and analytics are retained read-only.",
            "New report generation is paused.",
            "PDF downloads and enhancements are disabled.",
            "You can resume by re-subscribing — all historical data instantly returns to read+write mode.",
          ]} />
          <p>
            After the 1-year retention window, accounts are archived (see <a className="text-[#0A84FF] hover:underline" href="/privacy">Privacy Policy</a>).
            Reactivation after archival may require a fresh signup.
          </p>
        </LegalSection>

        <LegalSection id="how-to" title="5. How to Request a Refund">
          <p>Email us with the subject line "Refund Request" and include:</p>
          <LegalList items={[
            "The email address associated with your RADAR account.",
            "The transaction ID or invoice number.",
            "A brief reason for the refund (which exception applies).",
          ]} />
          <p>
            Send to <a className="text-[#0A84FF] hover:underline" href="mailto:support@radaracctintel.com">support@radaracctintel.com</a>.
            We respond within <strong className="text-white">1–2 business days</strong> with a
            decision and (if applicable) timeline.
          </p>
        </LegalSection>

        <LegalSection id="contact" title="6. Contact">
          <p>
            Questions about billing or this policy: <a className="text-[#0A84FF] hover:underline" href="mailto:support@radaracctintel.com">support@radaracctintel.com</a>.
          </p>
        </LegalSection>
      </LegalLayout>
    </>
  );
}
